import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {CircularProgress, Typography} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import Axios from "../util/Axios";
import CCButton from "../components/Common/CCButton";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import Palette from "../components/FormDesigner/Palette";
import Canvas from "../components/FormDesigner/Canvas";
import Inspector from "../components/FormDesigner/Inspector";
import FormRenderer from "../components/Forms/FormRenderer";
import PageHeader from "../components/Common/PageHeader";
import {
    addDisplay, addSection, emptyDefinition, inventField, itemsIn, moveInto, placeField,
    removeDisplay, removeField, removeSection, renameSection, sectionKeyFor, unplaced,
    updateDisplay, updatePlacement, targetSectionFor, hiddenButRequiredCodes, addTab, removeTab,
    renameTab, CanvasItem,
} from "../components/FormDesigner/designerModel";
import {DesignResponse, DisplayItem, FormDefinition, Placement, Section, Selection,
        ShapeField, Tab} from "../components/FormDesigner/types";
import {Grey7, Grey17} from "../theme/styles";
import {statusPill} from "../util/statusPill";

/**
 * Designing the form of ONE RECORD: the palette, the canvas and the inspector, over the copy of the
 * form that one draft revision carries. Ed, 2026-10-29: "A form may be extended in an object revision
 * by any member of the position that is the primary (they are the owners). The local edit of the
 * form should be as capable as the form designed route."
 *
 * THE SAME CANVAS AS THE FORM DESIGNER, over a different document: t1/v1/object_form_design reads
 * and writes the record's own SCHEMA instead of a FORM object's, and the server applies the
 * designer's rules unchanged. What differs is what is NOT here -- no shape chooser (a record was
 * created against its shape and keeps it), no Release or Revise (those are the record's own
 * lifecycle, on its page), no "Offered on" (a record is not offered to anybody). The server says
 * whether this caller may design it now (an owner, on a draft, editable for the persona they act
 * as) and why not otherwise; the page draws read-only on a no.
 *
 * THE DOCUMENT IN STATE IS THE DOCUMENT THE SERVER STORES. No translation layer, because a
 * translation is where the designer starts believing something the server does not — and the server
 * is the one that decides, overwriting what a placed field IS from the shape on every save.
 *
 * SAVING IS EXPLICIT. Auto-save on a canvas means a mis-drop is committed before it is noticed, and
 * a form is a definition other people's records are seeded from. The unsaved marker is the whole
 * safety net, so it has to be honest.
 *
 * THE SHAPE IS CHOSEN ONCE AND THEN LOCKED. Changing it would invalidate every placement in one
 * click, and there is no honest way to migrate them — the codes mean different things in a different
 * shape. Start another form instead; that is what several forms per type is for.
 */

export default function RecordFormDesignerPage() {
    const {usid} = useParams();
    const navigate = useNavigate();

    const [design, setDesign] = useState<(DesignResponse & {ObjectRevisionUSID?: string; ObjectID?: string | null; Reason?: string | null; DisplayRevision?: string | null}) | null>(null);
    const [definition, setDefinition] = useState<FormDefinition>(emptyDefinition());
    const [shapeFields, setShapeFields] = useState<ShapeField[]>([]);
    const [dataTypes, setDataTypes] = useState<any[]>([]);
    const [selection, setSelection] = useState<Selection>(null);
    const [openTab, setOpenTab] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    // Three views over one form: what it asks, what it looks like, and where it is offered.
    // A toggle rather than routes, because they are three readings of the same thing and switching
    // must not lose an unsaved canvas.
    const [view, setView] = useState<"design" | "preview">("design");
    // CANCEL, SAID INLINE (Ed, 2026-10-29: "This is not cancel button when going into form design
    // mode"). A cancel with unsaved work asks once, in the header, rather than with a browser dialog;
    // a cancel with nothing unsaved just goes back to the record's Form tab.
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const leave = () => navigate(`/object/${usid}`);
    const cancel = () => (dirty ? setConfirmingCancel(true) : leave());
    // Options per field, so a visibility condition on a picker offers its VALUES rather than asking
    // somebody to paste a key. The same call the preview uses, against the shape.
    const [fieldOptions, setFieldOptions] = useState<Record<string, any[]>>({});

    const editable = !!design?.Editable;

    const load = useCallback(() => {
        setLoading(true);
        Axios({method: "GET", url: "t1/v1/object_form_design", params: {ObjectRevisionUSID: usid}})
            .then((response: any) => {
                const body: DesignResponse = response.data;
                setDesign(body);
                setDefinition(body.Definition?.recordTypeRevisionUSID
                    ? body.Definition
                    : emptyDefinition(body.RecordTypeRevisionUSID));
                setShapeFields(body.ShapeFields ?? []);
                setDirty(false);
                setError(null);
                setLoading(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This record's form could not be opened.");
                setLoading(false);
            });
    }, [usid]);

    useEffect(load, [load]);

    // The chooser, and the type list for inventing a field. Both are catalogue reference data, so
    // they are fetched once rather than per interaction.
    useEffect(() => {
        Axios({method: "GET", url: "t1/v1/form_shapes", params: {DataTypes: true}})
            .then((r: any) => {
                // THE CATALOGUE ANSWERS WITH TYPE REVISIONS -- ListOfDataTypeRevision, each row the
                // revision in force with the type's name and kind joined on -- and a revision USID is
                // exactly what an invented field binds to. This read the list under the wrong tag
                // from the day it was written, so New field never offered a type (found 2026-10-29,
                // opening the designer on a record at Peak).
                const message = r.data?.PlatformMessage ?? {};
                const raw = message.ListOfDataTypeRevision?.DataTypeRevision ?? message.ListOfDataType?.DataType ?? [];
                const list = (Array.isArray(raw) ? raw : [raw]).filter(Boolean).map((t: any) => ({
                    USID: t.USID, Name: t.TypeName ?? t.Name, Kind: t.Kind, Code: t.TypeCode ?? t.Code,
                }));
                list.sort((a: any, b: any) => String(a.Name ?? "").localeCompare(String(b.Name ?? "")));
                setDataTypes(list);
            })
            .catch(() => setDataTypes([]));
    }, []);

    const change = (next: FormDefinition) => { setDefinition(next); setDirty(true); };


    const save = () => {
        setSaving(true);
        Axios({method: "PUT", url: "t1/v1/object_form_design",
               data: {ObjectRevisionUSID: usid, Definition: definition}})
            .then((response: any) => {
                // TAKE THE SERVER'S VERSION BACK, not just a success flag. It rewrote every declared
                // field from the shape, so the canvas would otherwise go on showing what was sent.
                setDefinition(response.data.Definition);
                setDirty(false);
                setError(null);
                setSaving(false);
                // The server's list of what still differs, after this save: a realigned field drops
                // off it, a kept one stays with its mark.
                load();
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This record's form could not be saved.");
                setSaving(false);
            });
    };


    useEffect(() => {
        const shape = definition.recordTypeRevisionUSID;
        if (!shape) return;
        Axios({method: "POST", url: "t1/v1/object_form_definition",
               data: {CompositeRevisionUSID: shape}})
            .then((r: any) => {
                const byCode: Record<string, any[]> = {};
                (r.data?.Fields ?? []).forEach((f: any) => { byCode[f.Code] = f.Options ?? []; });
                setFieldOptions(byCode);
            })
            .catch(() => setFieldOptions({}));
    }, [definition.recordTypeRevisionUSID]);

    const palette = useMemo(() => unplaced(definition, shapeFields), [definition, shapeFields]);
    // Where a CLICK on the palette lands. Dragging carries its own section; clicking has to be
    // told, and telling it "the first section" meant a two-section form could only be filled by
    // dragging. See targetSectionFor.
    const target = targetSectionFor(definition, selection);
    const targetTitle = (definition.sections ?? []).find(s => s.key === target)?.title ?? target;
    const atRisk = hiddenButRequiredCodes(definition);

    // The blue bar names the SECTION and is the one part of this page that does not depend on which
    // form is open -- so it wraps the loading and not-found states too, rather than appearing only
    // once a design has loaded and making the page look like it changed identity.
    const page = (content: React.ReactNode) => (
        <div style={{height: "calc(100vh - 64px)", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Form Designer · this record"/>
            {content}
        </div>
    );

    if (loading) {
        return page(<div style={{padding: 40}}><CircularProgress size={22}/></div>);
    }

    if (!design) {
        return page(<Typography style={{padding: 40, fontSize: 14, color: "#B3261E"}}>{error}</Typography>);
    }

    return page(
        <div style={{padding: "20px 28px", flex: 1, minHeight: 0, display: "flex",
                     flexDirection: "column"}}>
            {/* Header */}
            <div style={{display: "flex", alignItems: "center", gap: 14, marginBottom: 6}}>
                <ArrowBack fontSize="small" style={{cursor: "pointer", color: Grey7}} onClick={cancel}/>
                <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>
                    {design.ObjectID ? `${design.ObjectID} — ` : ""}{design.Name}
                </Typography>
                {statusPill(design.Status)}
                <Typography style={{fontSize: 13, color: Grey7}}>Rev {design.DisplayRevision ?? design.Revision}</Typography>
                {!editable && design.Reason && (
                    <Typography style={{fontSize: 13, color: "#B26500"}}>{design.Reason}</Typography>
                )}
                <div style={{flex: 1}}/>
                {/* The guide lived on the old bespoke list page; it belongs where the work happens. */}
                <CCButton className={css_buttons.whiteStandardButton}
                          onClick={() => window.open("/docs/form-designer.html", "cresFormDesignerDocs",
                                                     "width=1200,height=900,scrollbars=yes,resizable=yes")}>
                    Guide
                </CCButton>
                {dirty && <Typography style={{fontSize: 13, color: "#B26500"}}>Unsaved changes</Typography>}
                {(["design", "preview"] as const).map(v => (
                    <CCButton key={v}
                              className={view === v ? css_buttons.blueStandardButton
                                                    : css_buttons.whiteStandardButton}
                              onClick={() => setView(v)}>
                        {v === "design" ? "Design" : "Preview"}
                    </CCButton>
                ))}
                {editable && (
                    <CCButton className={css_buttons.blueStandardButton} disabled={saving || !dirty}
                              onClick={save}>{saving ? "Saving…" : "Save"}</CCButton>
                )}
                {confirmingCancel ? (
                    <>
                        <Typography style={{fontSize: 13, color: "#B26500"}}>Discard unsaved changes?</Typography>
                        <CCButton className={css_buttons.whiteStandardButton} onClick={leave}>Discard</CCButton>
                        <CCButton className={css_buttons.whiteStandardButton} onClick={() => setConfirmingCancel(false)}>Keep editing</CCButton>
                    </>
                ) : (
                    <CCButton className={css_buttons.whiteStandardButton} disabled={saving} onClick={cancel}>Cancel</CCButton>
                )}

            </div>

            {error && <Typography style={{fontSize: 13, color: "#B3261E", marginBottom: 8}}>{error}</Typography>}

            {/* A record has no shape to choose: it was created against one and keeps it. */}
            {!definition.recordTypeRevisionUSID ? (
                <Typography style={{fontSize: 14, color: "#B3261E", marginTop: 24}}>
                    This record has no shape, so its form has nothing to be designed against.
                </Typography>
            ) : view === "preview" ? (
                <div style={{overflowY: "auto", paddingTop: 12, maxWidth: 900}}>
                    {/* The real renderer, over THIS RECORD and its own definition as last saved:
                        controls and options for real, which the canvas deliberately does not draw. */}
                    <FormRenderer key={`preview-${saving}`} objectRevisionUSID={usid!} readOnly value={{}}/>
                </div>
            ) : (
                <>
                    {atRisk.length > 0 && (
                        <Typography style={{fontSize: 12, color: "#B3261E", marginBottom: 8}}>
                            {atRisk.length === 1
                                ? `1 question can be required while hidden.`
                                : `${atRisk.length} questions can be required while hidden.`}{" "}
                            The save refuses for a question nobody can see. Select
                            {atRisk.length === 1 ? " it" : " them"} to fix the rule.
                        </Typography>
                    )}
                    <div style={{display: "flex", flex: 1, minHeight: 0, gap: 4}}>
                        <Palette
                            fields={palette}
                            dataTypes={dataTypes}
                            disabled={!editable}
                            targetSection={targetTitle}
                            onPlace={f => change(placeField(definition, f, target, shapeFields))}
                            onInvent={(code, name, type) =>
                                change(inventField(definition, code, name, type, target))}
                            onAddDisplay={kind => change(addDisplay(definition, kind, target))}
                            inventNote="Asked on this record only. The Form tab offers Promote when it should be on the shape for everyone."
                        />
                        <Canvas
                            definition={definition}
                            selection={selection}
                            disabled={!editable}
                            openTab={openTab ?? (definition.tabs ?? [])[0]?.key ?? null}
                            onOpenTab={setOpenTab}
                            onAddTab={() => {
                                const title = `Tab ${(definition.tabs ?? []).length + 1}`;
                                const key = sectionKeyFor(title, (definition.tabs ?? []) as any);
                                change(addTab(definition, key, title));
                                setOpenTab(key);
                                setSelection({kind: "tab", key});
                            }}
                            onSelect={setSelection}
                            onAddSection={() => {
                                const title = `Section ${(definition.sections ?? []).length + 1}`;
                                const key = sectionKeyFor(title, definition.sections ?? []);
                                const tab = openTab ?? (definition.tabs ?? [])[0]?.key ?? null;
                                // Into the tab being looked at. Adding a section while a tab is open
                                // and having it appear somewhere else is the same surprise the
                                // palette used to spring by always using the first section.
                                change(renameSection(addSection(definition, key, title), key, {tab}));
                                setSelection({kind: "section", key});
                            }}
                            onRemoveSection={key => {
                                change(removeSection(definition, key));
                                setSelection(null);
                            }}
                            onRemoveField={code => {
                                change(removeField(definition, code));
                                setSelection(null);
                            }}
                            onRemoveDisplay={index => {
                                change(removeDisplay(definition, index));
                                setSelection(null);
                            }}
                            onMove={(moving, section, before) =>
                                change(moveInto(definition, moving, section, before))}
                            onDropField={(code, section, before) => {
                                const field = shapeFields.find(f => f.Code === code);
                                if (!field) return;
                                const placed = placeField(definition, field, section, shapeFields);
                                const item = itemsIn(placed, section).find(
                                    i => i.kind === "field" && (i as any).placement.code === code);
                                change(item ? moveInto(placed, item as CanvasItem, section, before) : placed);
                            }}
                            onDropDisplay={(kind, section, before) => {
                                const added = addDisplay(definition, kind as DisplayItem["kind"], section);
                                const items = itemsIn(added, section);
                                const item = items[items.length - 1];
                                change(item ? moveInto(added, item, section, before) : added);
                            }}
                        />
                        <Inspector
                            definition={definition}
                            selection={selection}
                            disabled={!editable}
                            shapeFields={shapeFields}
                            fieldOptions={fieldOptions}
                            ownDefinitions={design.Differs ?? []}
                            onPlacement={(code, patch: Partial<Placement>) =>
                                change(updatePlacement(definition, code, patch))}
                            onDisplay={(index, patch: Partial<DisplayItem>) =>
                                change(updateDisplay(definition, index, patch))}
                            onSection={(key, patch: Partial<Section>) =>
                                change(renameSection(definition, key, patch))}
                            onTab={(key, patch: Partial<Tab>) =>
                                change(renameTab(definition, key, patch))}
                            onRemoveTab={key => {
                                change(removeTab(definition, key));
                                setOpenTab(null);
                                setSelection(null);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
